/* jshint esversion: 6 */
import FocusRecoveredInvigilatorModalController from '../../modules/invigilatorMenu/modalCtrl/FocusRecoveredInvigilatorModalController';

/**
 * FocusRecoveredService is a service class responsible for handling focus recovery events.
 * It manages modal displays, notifications, and logging based on focus loss durations.
 */
class FocusRecoveredService {
    /**
     * Constructs the FocusRecoveredService instance.
     * @param {Object} $bridges - Service for bridge operations.
     * @param {Object} $uibModal - Service for modal dialogs.
     * @param {Object} $signals - Service for signal emissions.
     * @param {Object} $route - Service for routing.
     * @param {Object} $rootScope - Root scope service.
     * @param {Object} $translate - Translation service.
     * @param {Object} TaskInfoService - Service for task information.
     * @param {Object} $injector - Dependency injection service.
     */
    constructor($bridges, $uibModal, $signals, $route, $rootScope, $translate, TaskInfoService, $injector) {
        this.$bridges = $bridges;
        this.$uibModal = $uibModal;
        this.$signals = $signals;
        this.$route = $route;
        this.$rootScope = $rootScope;
        this.$translate = $translate;
        this.TaskInfoService = TaskInfoService;
        this.$injector = $injector;

        this._logIdentifier = 'FocusRecoveredService';
        this.$logger = this.$bridges.logger();
        this.$eventLog = this.$bridges.eventLog();
        this._onFocusRecoveredModalOpened = false;
        this._focusRecoveredModalInstance = null;
        this._lossOfFocus = [];
        this._lossOfFocusConfig = null; // = TaskInfoService.getTaskInfo().getLossOfFocusConfig();
        this.$logger.info(this, 'initialize');
    }

    /**
     * Checks if the current route indicates that the task is done.
     * @param {Object} current - The current route object.
     * @returns {boolean} - True if the task is done, otherwise false.
     */
    _isTaskDone(current) {
        if (!current) {
            return this.$route.current.regexp.test("/taskdone/:UserUUID");
        }
        return current.$$route.originalPath === "/taskdone/:UserUUID" || current.$$route.originalPath === "/journalOptIn/";
    }

    /**
     * Gets the Notification service instance.
     * @returns {Object} - The Notification service instance.
     */
    _Notification() {
        if (!this._NotificationInstance) {
            this._NotificationInstance = this.$injector.get('Notification');
        }
        return this._NotificationInstance;
    }

    /**
     * Handles the focus recovered event by opening a modal if the focus loss duration is significant.
     * @param {number} focusLostInMs - The duration of focus loss in milliseconds.
     */
    _onFocusRecovered(focusLostInMs) {
        this._lossOfFocus.push(focusLostInMs);
        console.log('_onFocusRecovered focusLostInMs:', focusLostInMs /*,JSON.stringify(this._lossOfFocus)*/);

        if (!this._onFocusRecoveredModalOpened) {
            console.log('_onFocusRecovered opening modal(?)');
            this.$signals.emit('readingTimeUnlockKeyboard');

            if (focusLostInMs < this._lossOfFocusConfig["lossOfFocusDisplayFromMs"]) {
                console.log('_onFocusRecovered too short, not opening modal');
                this._lossOfFocusEvents(true);
            } else {
                console.log('_onFocusRecovered actually opening modal');
                this._onFocusRecoveredModalOpened = true;

                this._focusRecoveredModalInstance = this.$uibModal.open({
                    templateUrl: "/views/ib/modules/invigilatorMenu/modal/focusRecoveredInvigilatorModal.html",
                    windowClass: "focus-recovered-modal",
                    transclude: false,
                    backdrop: 'static',
                    keyboard: false,
                    controller: FocusRecoveredInvigilatorModalController
                });

                this._focusRecoveredModalInstance.result.then(() => {
                    console.log('_onFocusRecovered modal success');
                    this.$signals.emit('readingTimeLockKeyboard');
                    this._onFocusRecoveredModalOpened = false;
                    this._lossOfFocusEvents(false);
                }, () => {
                    console.log('_onFocusRecovered modal fail');
                    this._onFocusRecoveredModalOpened = false;
                    this._lossOfFocusEvents(false);
                });
            }
        }
    }

    /**
     * Handles the focus recovered event by showing a notification if the focus loss duration is significant.
     * @param {number} focusLostInMs - The duration of focus loss in milliseconds.
     */
    _onFocusRecoveredAlternative(focusLostInMs) {
        this._lossOfFocus.push(focusLostInMs);
        console.log(this.TaskInfoService.getTaskInfo().isAlternativeLossOfFocus());

        if (focusLostInMs < this._lossOfFocusConfig["lossOfFocusDisplayFromMs"]) {
            console.log('_onFocusRecovered too short, not opening modal');
            this._lossOfFocusEvents(true);
            return;
        }

        let focusLostMS = this._lossOfFocus.reduce((accumulator, value) => accumulator + value, 0);
        let focusLostInSeconds = Math.floor(focusLostMS / 1000);

        let translationVariables = {
            time: this._lossOfFocus.length,
            sec: focusLostInSeconds,
        };

        let notificationObject = new (this._Notification())({
            templateUrl: '/views/ib/notifications/lossOfFocusNotification/lossOfFocusAlternative.html',
            message: String(translationVariables.sec),
            title: String(translationVariables.time),
            closeOnClick: true,
            delay: false,
            positionX: 'right',
            positionY: 'bottom',
            replaceMessage: true
        });

        notificationObject.then(() => {
            this._lossOfFocusEvents(false, "focusLostAlternative");
        });
    }

    /**
     * Activates the focus recovered event listeners based on the task configuration.
     */
    _activate() {
        if (this.$signals.getSubscriberId('onFocusRecovered').length === 0) {
            if (this.TaskInfoService.getTaskInfo().isAlternativeLossOfFocus()) {
                this.$signals.on('onFocusRecovered', this._onFocusRecoveredAlternative.bind(this));
            } else {
                this.$signals.on('onFocusRecovered', this._onFocusRecovered.bind(this));
            }
        }
    }

    /**
     * Deactivates the focus recovered event listeners.
     */
    _deactivate() {
        if (this._focusRecoveredModalInstance) {
            this._focusRecoveredModalInstance.close();
            this._lossOfFocusEvents("");
        }
        if (this.TaskInfoService.getTaskInfo().isAlternativeLossOfFocus()) {
            this.$signals.remove('onFocusRecovered', this._onFocusRecoveredAlternative.bind(this));
        } else {
            this.$signals.remove('onFocusRecovered', this._onFocusRecovered.bind(this));
        }
    }

    /**
     * Logs the focus loss events based on the duration and type.
     * @param {boolean} focusLostShort - Indicates if the focus loss was short.
     * @param {string} focusLostRegularEventType - The type of focus loss event.
     */
    _lossOfFocusEvents(focusLostShort, focusLostRegularEventType) {
        if (typeof (focusLostRegularEventType) === 'undefined') {
            focusLostRegularEventType = 'focusLost';
        }

        console.log('_lossOfFocusEvents', focusLostShort);

        if (this._lossOfFocus.length === 0) return false;

        let focusLostMS = this._lossOfFocus.reduce((accumulator, value) => accumulator + value, 0);
        let focusLostInSeconds = Math.floor(focusLostMS / 1000);

        console.log('_lossOfFocusEvents total', focusLostMS, 'in seconds', focusLostInSeconds);

        let translationVariables = {
            time: this._lossOfFocus.length,
            sec: focusLostInSeconds,
        };

        if (focusLostShort) {
            console.log('_lossOfFocusEvents logging short');
            this.$eventLog.logEvent(this.$translate.instant(
                "MODULES.ADMIN.EVENTLOG_APPLICATION_FOCUS_LOST_SHORT",
                translationVariables
            ), 'focusLostShort', '', '', {
                duration: focusLostInSeconds,
            });
        } else {
            console.log('_lossOfFocusEvents logging normal');
            this.$eventLog.logEvent(this.$translate.instant(
                "MODULES.ADMIN.EVENTLOG_APPLICATION_FOCUS_LOST",
                translationVariables
            ), focusLostRegularEventType, '', '', {
                duration: focusLostInSeconds,
            });
        }
        this._lossOfFocus = [];
    }

    /**
     * Removes the notification element if it exists.
     */
    removeNotification() {
        if (document.getElementById('notfication-alternative-fol')) {
            document.getElementById('notfication-alternative-fol').click();
        }
    }

    /**
     * Initializes the service by setting up the focus loss configuration and activating or deactivating based on the current route.
     */
    init() {
        this._lossOfFocusConfig = this.TaskInfoService.getTaskInfo().getLossOfFocusConfig();

        if (this._isTaskDone()) {
            this._deactivate();
        } else {
            this._activate();
        }

        this.$rootScope.$on('$routeChangeStart', (next, current) => {
            if (this._isTaskDone(current)) {
                this._deactivate();
            } else {
                this._activate();
            }
        });
    }
}

export default FocusRecoveredService;